import { DateTime } from "luxon";

export const propertyHomeTypeList = [
  { value: "APARTMENTS", label: "Apartment" },
  { value: "CONDOS_COOPS", label: "Condo" },
  { value: "DEVELOPMENT", label: "Development" },
  { value: "HOUSE", label: "House" },
  { value: "LOTS_LANDS", label: "Land" },
  { value: "MANUFACTURED", label: "Manufactured" },
  { value: "MULTI_FAMILY", label: "Multi Family" },
  { value: "RESIDENCE", label: "Residence" },
  { value: "TOWNHOUSE", label: "Townhouse" }
];

export const getPropertyHomeTypeList = (t) => [
  { value: "APARTMENTS", label: t("addProperty.homeTypes.apartment") },
  { value: "CONDOS_COOPS", label: t("addProperty.homeTypes.condo") },
  { value: "DEVELOPMENT", label: t("addProperty.homeTypes.development") },
  { value: "HOUSE", label: t("addProperty.homeTypes.house") },
  { value: "LOTS_LANDS", label: t("addProperty.homeTypes.land") },
  { value: "MANUFACTURED", label: t("addProperty.homeTypes.manufactured") },
  { value: "MULTI_FAMILY", label: t("addProperty.homeTypes.multiFamily") },
  { value: "RESIDENCE", label: t("addProperty.homeTypes.residence") },
  { value: "TOWNHOUSE", label: t("addProperty.homeTypes.townhouse") }
];

export function generatePropertyHomeTypeMap(list) {
  return list.reduce((acc, item) => {
    acc[item.value] = item.label;
    return acc;
  }, {});
}

export const propertyHomeTypeMap =
  generatePropertyHomeTypeMap(propertyHomeTypeList);

export const getPropertyTypeListingType = (t) => [
  {
    value: "SELL",
    label: t("common.forSell")
  },
  {
    value: "RENT",
    label: t("common.forRent")
  }
];

export * from "./currencyFormat";
export * from "./objectToQueryParams";
export * from "./getQueryParams";

export const formatUtcToLocalTime = (utcDate) => {
  return DateTime.fromISO(utcDate, { zone: "utc" }).toLocal();
};

export const socketComingMessageParser = (messageStr) => {
  messageStr = messageStr.replace(/([^,])\s*\)$/, "$1,)");

  const regex = /(\w+)=([^,()]+(?:\([^)]*\))?)/g;
  const result = {};
  let match;

  while ((match = regex.exec(messageStr)) !== null) {
    const key = match[1];
    let value = match[2].trim();

    // Find the exact value by checking the next key position
    const remainingString = messageStr.substring(match.index + match[0].length);
    const nextKeyMatch = remainingString.match(/\b\w+=/);
    const nextKeyIndex = nextKeyMatch
      ? match.index + match[0].length + nextKeyMatch.index
      : messageStr.length;

    value = messageStr
      .substring(match.index + key.length + 1, nextKeyIndex)
      .trim();

    value = value.replace(/[\s,)\]]+$/, "");

    if (value.startsWith("(") && value.endsWith(")")) {
      value = value.slice(1, -1);
    }

    result[key] = value;
  }

  // Extract messageText if present
  const messageTextStart = messageStr.indexOf("messageText=");
  if (messageTextStart !== -1) {
    const messageTextEnd = messageStr.indexOf(", sendAt=", messageTextStart);
    const messageText = messageStr
      .slice(messageTextStart + "messageText=".length, messageTextEnd)
      .trim()
      .replace(/[\s,)\]]+$/, "");
    result.messageText = messageText;
  }

  return result;
};

export const getMessageTimeLabel = (lastMessageTime, t) => {
  if (!lastMessageTime) return "";

  const cleanedTime = lastMessageTime.split(".")[0];
  const now = DateTime.now();
  const messageTime = DateTime.fromISO(cleanedTime, { zone: "utc" }).toLocal();

  const timeDifference = now.diff(messageTime, "hours").hours;

  if (timeDifference < 24 && now.hasSame(messageTime, "day")) {
    return `${t("inbox.today")} |`;
  } else if (timeDifference >= 24 && timeDifference < 48) {
    return `${t("inbox.yesterday")} |`;
  }

  if (timeDifference >= 2 && timeDifference < 7) {
    return `${t("inbox.aFewDaysAgo")} |`;
  }

  if (timeDifference >= 7) {
    return `${t("inbox.lastWeek")} |`;
  }

  return `${t("inbox.older")} |`;
};

export const convertStringToJson = (str) => {
  const keyValue = str?.replace(/[{}]/g, "").split("=");

  const jsonObject = {};
  jsonObject[keyValue[0]] = keyValue[1]?.toLowerCase() === "true";

  return jsonObject;
};

export const timeSinceMessage = (messageTime) => {
  const cleanMessageTime = messageTime.split(".")[0];
  const now = DateTime.now();
  const messageDate = DateTime.fromISO(cleanMessageTime);

  const diffInMinutes = now.diff(messageDate, "minutes").minutes;

  if (diffInMinutes < 1) {
    return "Just now";
  }

  if (diffInMinutes < 60) {
    return `${Math.floor(diffInMinutes)} min`;
  }

  const diffInHours = now.diff(messageDate, "hours").hours;
  if (diffInHours < 24) {
    return `${Math.floor(diffInHours)} hrs`;
  }

  return messageDate.toLocaleString(DateTime.DATETIME_MED);
};

export const getTimePassed = (notificationTime) => {
  const notificationDateTime = DateTime.fromISO(notificationTime, {
    zone: "utc"
  }).toLocal();
  const now = DateTime.local();

  const diffInSeconds = now
    .diff(notificationDateTime, "seconds")
    .toObject().seconds;

  if (diffInSeconds < 0) return "Just now";

  if (diffInSeconds < 60) {
    return `${Math.floor(diffInSeconds)} sec`;
  } else if (diffInSeconds < 3600) {
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    return `${diffInMinutes} min`;
  } else if (diffInSeconds < 86400) {
    const diffInHours = Math.floor(diffInSeconds / 3600);
    return `${diffInHours} hrs`;
  } else {
    return notificationDateTime.toFormat("hh:mm a");
  }
};

export const getListingLabel = (propertyListingType, t) => {
  switch (propertyListingType) {
    case "SELL":
      return t("common.forSell");
    case "RENT":
      return t("common.forRent");
    default:
      return "-";
  }
};
// utils/filterUtils.js

export const countAppliedFilters = (filters) => {
  if (!filters) return 0;

  const filterKeys = [
    "minPropertySize",
    "maxPropertySize",
    "maxNoBathroom",
    "maxNoBedroom",
    "maxNoKitchen",
    "propertySizePostfix"
  ];

  return filterKeys.reduce((count, key) => {
    if (
      filters[key] !== undefined &&
      filters[key] !== null &&
      filters[key] !== "" &&
      filters[key] !== 0
    ) {
      return count + 1;
    }
    return count;
  }, 0);
};

export const getTranslatedAreaUnit = (unitFromBE, t) => {
  if (!unitFromBE) return "";

  const normalized = unitFromBE.toLowerCase();

  const unitKeyMap = {
    "sq yard": "sqYard",
    "sq feet": "sqFeet",
    feet: "feet",
    meters: "meters"
  };

  const postfixKey = unitKeyMap[normalized];

  return postfixKey ? t(`common.areaUnits.${postfixKey}`) : unitFromBE;
};

export const userRoleTranslationKeys = (t) => ({
  "Real Estate Agent": t("signUp.userRoles.realEstateAgent"),
  "Real Estate Developer": t("signUp.userRoles.realEstateDeveloper"),
  "Property Manager": t("signUp.userRoles.propertyManager"),
  Architect: t("signUp.userRoles.architect"),
  "Lawyer/Real Estate Attorney": t("signUp.userRoles.lawyer"),
  "Financial Services": t("signUp.userRoles.financialServices")
});

export const PASSWORD_REGEX =
  /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]{8,}$/;
